import { useEffect, useMemo, useRef, useState } from 'react'
import { ChevronDown } from 'lucide-react'
import type { Forklift } from '@/types/forklift'

interface ForkliftMultiSelectProps {
  forklifts: Forklift[]
  selected: string[]
  onChange: (forkIds: string[]) => void
  disabled?: boolean
  className?: string
}

// メトリクス画面の機体選択。1 台 / 任意の複数台 / 「すべて」をチェックボックスで選ぶ。
// 「すべて」は一覧にある機体を全部選んだ状態と同義で、一覧 (SIM 表示の切り替えなど) が
// 変わっても選択済み ID はそのまま持ち、一覧に無い ID は表示上は数えない
export default function ForkliftMultiSelect({
  forklifts,
  selected,
  onChange,
  disabled = false,
  className = '',
}: ForkliftMultiSelectProps) {
  const [open, setOpen] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return
    const handlePointerDown = (e: MouseEvent) => {
      if (!containerRef.current?.contains(e.target as Node)) {
        setOpen(false)
      }
    }
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false)
    }
    document.addEventListener('mousedown', handlePointerDown)
    document.addEventListener('keydown', handleKeyDown)
    return () => {
      document.removeEventListener('mousedown', handlePointerDown)
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [open])

  const allIds = useMemo(() => forklifts.map((f) => f.fork_id), [forklifts])
  const selectedSet = useMemo(() => new Set(selected), [selected])
  const selectedInList = allIds.filter((id) => selectedSet.has(id))
  const isAll = allIds.length > 0 && selectedInList.length === allIds.length

  const label =
    selectedInList.length === 0
      ? '選択してください'
      : isAll
        ? `すべて (${allIds.length}台)`
        : selectedInList.length === 1
          ? selectedInList[0]
          : `${selectedInList.length}台を選択`

  const toggleAll = () => {
    onChange(isAll ? [] : allIds)
  }

  const toggleOne = (forkId: string) => {
    if (selectedSet.has(forkId)) {
      onChange(selected.filter((id) => id !== forkId))
    } else {
      // 一覧の並び順で保持する (表示名や CSV の並びが安定する)
      const next = new Set([...selected, forkId])
      onChange(allIds.filter((id) => next.has(id)))
    }
  }

  return (
    <div ref={containerRef} className={`relative ${className}`}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
        title={
          selectedInList.length > 1 ? selectedInList.join(', ') : undefined
        }
        className="flex items-center justify-between gap-2 px-4 py-2 bg-white border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 min-w-[220px] text-left disabled:opacity-50 disabled:cursor-not-allowed"
      >
        <span
          className={
            selectedInList.length === 0 ? 'text-gray-500' : 'text-gray-900'
          }
        >
          {label}
        </span>
        <ChevronDown className="h-4 w-4 text-gray-400 shrink-0" />
      </button>
      {open && (
        <div
          role="listbox"
          aria-multiselectable
          className="absolute left-0 mt-1 min-w-full max-h-80 overflow-y-auto bg-white border border-gray-200 rounded-lg shadow-lg py-1 z-20"
        >
          <label className="flex items-center gap-2 px-4 py-2 text-sm text-gray-900 font-medium hover:bg-gray-50 cursor-pointer border-b border-gray-100">
            <input
              type="checkbox"
              checked={isAll}
              onChange={toggleAll}
              className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
            />
            すべて
            <span className="text-xs text-gray-400 font-normal">
              {allIds.length}台を合算
            </span>
          </label>
          {forklifts.map((f) => (
            <label
              key={f.fork_id}
              className="flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 cursor-pointer whitespace-nowrap"
            >
              <input
                type="checkbox"
                checked={selectedSet.has(f.fork_id)}
                onChange={() => toggleOne(f.fork_id)}
                className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
              />
              {f.fork_id}
              <span className="text-xs text-gray-400">({f.site_id})</span>
            </label>
          ))}
          {forklifts.length === 0 && (
            <p className="px-4 py-2 text-sm text-gray-400">機体がありません</p>
          )}
        </div>
      )}
    </div>
  )
}
